import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import {
  CandidateViewKind,
  stageViewKey,
  type CandidateViewCounts,
  type CandidateViewKey,
  type HiringStage,
} from "./candidate-views";

type NavEntry = {
  key: CandidateViewKey;
  label: string;
  count: number;
  hint?: string;
};

const WITHDRAWN_HINT = "last 30 days";

function getNavEntries({
  counts,
  stages,
}: {
  counts: CandidateViewCounts;
  stages: HiringStage[];
}): { primaryEntries: NavEntry[]; archiveEntries: NavEntry[] } {
  return {
    primaryEntries: [
      { key: CandidateViewKind.All, label: "All", count: counts.all },
      ...stages.map((stage) => ({
        key: stageViewKey(stage.id),
        label: stage.name,
        count: counts.byStageId[stage.id] ?? 0,
      })),
      { key: CandidateViewKind.Hired, label: "Hired", count: counts.hired },
    ],
    archiveEntries: [
      {
        key: CandidateViewKind.Rejected,
        label: "Rejected",
        count: counts.rejected,
      },
      {
        key: CandidateViewKind.Withdrawn,
        label: "Withdrawn",
        count: counts.withdrawn,
        hint: WITHDRAWN_HINT,
      },
    ],
  };
}

function NavButton({
  entry,
  isSelected,
  onSelect,
}: {
  entry: NavEntry;
  isSelected: boolean;
  onSelect: (view: CandidateViewKey) => void;
}) {
  return (
    <li>
      <button
        type="button"
        aria-current={isSelected ? "page" : undefined}
        onClick={() => onSelect(entry.key)}
        className={cn(
          "flex min-h-11 w-full items-center justify-between gap-3 rounded-xl border px-3 py-2 text-left text-sm font-medium focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary",
          isSelected
            ? "border-slate-200 bg-white text-primary shadow-xs"
            : "border-transparent text-slate-600 hover:bg-slate-100",
        )}
      >
        <span className="min-w-0 [overflow-wrap:anywhere]">
          {entry.label}
          {entry.hint && (
            <span className="block text-xs font-normal text-slate-400">
              {entry.hint}
            </span>
          )}
        </span>
        <span
          className={cn(
            "shrink-0 text-sm tabular-nums",
            isSelected ? "text-primary" : "text-slate-400",
          )}
        >
          {entry.count}
        </span>
      </button>
    </li>
  );
}

export function CandidateViewNav({
  view,
  onSelectView,
  counts,
  stages,
}: {
  view: CandidateViewKey;
  onSelectView: (view: CandidateViewKey) => void;
  counts: CandidateViewCounts;
  stages: HiringStage[];
}) {
  const { primaryEntries, archiveEntries } = getNavEntries({ counts, stages });

  return (
    <>
      {/* Mobile: a single select keeps the list of stages out of the way. */}
      <div className="lg:hidden">
        <Select
          value={view}
          onValueChange={(next) => onSelectView(next as CandidateViewKey)}
        >
          <SelectTrigger
            aria-label="Candidate view"
            className="min-h-11 bg-white"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {[...primaryEntries, ...archiveEntries].map((entry) => (
              <SelectItem key={entry.key} value={entry.key}>
                {entry.label} ({entry.count})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <nav
        aria-label="Candidate views"
        className="sticky top-4 hidden w-72 shrink-0 self-start lg:block"
      >
        <ul className="space-y-1">
          {primaryEntries.map((entry) => (
            <NavButton
              key={entry.key}
              entry={entry}
              isSelected={entry.key === view}
              onSelect={onSelectView}
            />
          ))}
        </ul>
        <h2 className="mb-1 mt-6 px-3 text-xs font-semibold uppercase tracking-wide text-slate-400">
          Review archive
        </h2>
        <ul className="space-y-1">
          {archiveEntries.map((entry) => (
            <NavButton
              key={entry.key}
              entry={entry}
              isSelected={entry.key === view}
              onSelect={onSelectView}
            />
          ))}
        </ul>
      </nav>
    </>
  );
}
